import { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import { Tender, Tendering, TenderingIndexing as TenderingIndexingInterface } from "../../../models/tendering";
import { Badge, BadgeProps, Body1Strong, Button, Card, Divider, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Title3, Text, Caption1, CardPreview, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Tooltip } from "@fluentui/react-components";
import { AddCircleFilled, DeleteRegular, EditRegular, InfoRegular, LockClosedRegular } from "@fluentui/react-icons";
import CreateIndexing from "../dialogs/CreateIndexing";
import { getIndexingPriceChange, getRequestedIndexings, getTodayKey, toDateKey } from "../../../utils/tendering-utils";

interface TenderingIndexingProps {
  tenderingDocument: Tendering;
  tenders: Tender[];
  updateTenderingData: Dispatch<SetStateAction<Tendering>>;
}

interface IndexingResultRow {
  tenderName: string;
  question: string;
  before?: number;
  after?: number;
}

const getStatus = (indexing: TenderingIndexingInterface): { label: string; color: BadgeProps["color"] } => {
  const today = getTodayKey();
  if (indexing.indexingDate && today >= toDateKey(indexing.indexingDate)) return { label: "Genomförd", color: "success" };
  if (indexing.endDate && today > toDateKey(indexing.endDate)) return { label: "Stängd", color: "warning" };
  if (indexing.startDate && today >= toDateKey(indexing.startDate)) return { label: "Öppen", color: "brand" };
  return { label: "Planerad", color: "informative" };
};

// Locked once opened so suppliers request under the same terms that are later applied
const isLocked = (indexing: TenderingIndexingInterface): boolean =>
  !!indexing.startDate && getTodayKey() >= toDateKey(indexing.startDate);

const isDone = (indexing: TenderingIndexingInterface): boolean =>
  !!indexing.indexingDate && getTodayKey() >= toDateKey(indexing.indexingDate);

const formatDate = (date?: string | Date): string =>
  date ? new Date(date).toLocaleDateString("sv-SE") : "-";

const TenderingIndexing: FC<TenderingIndexingProps> = ({
    tenderingDocument,
    tenders,
    updateTenderingData
}) => {
  const indexings = tenderingDocument.indexing || [];

  const [selectedIndexingID, setSelectedIndexingID] = useState<number | null>(() => {
    const latest = [...indexings].sort((a, b) => toDateKey(b.startDate).localeCompare(toDateKey(a.startDate)))[0];
    return latest ? latest.indexingID : null;
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingIndexing, setEditingIndexing] = useState<TenderingIndexingInterface | null>(null);
  const [deletingIndexing, setDeletingIndexing] = useState<TenderingIndexingInterface | null>(null);

  const selectedIndexing = indexings.find(i => i.indexingID === selectedIndexingID) || null;

  const getRequests = (indexingID: number) =>
    tenders.flatMap(tender => (tender.indexing || []).filter(i => Number(i.indexingID) === indexingID));

  const requests = useMemo(() => {
    if (!selectedIndexing) return [];
    return getRequests(selectedIndexing.indexingID)
      .sort((a, b) => new Date(b.requestDate ?? 0).getTime() - new Date(a.requestDate ?? 0).getTime());
  }, [selectedIndexing, tenders]);

  const results = useMemo((): IndexingResultRow[] => {
    if (!selectedIndexing || !isDone(selectedIndexing)) return [];
    const rows: IndexingResultRow[] = [];
    requests.forEach(request => {
      const tender = tenders.find(t => t.tenderID === request.tenderID);
      if (!tender) return;
      const requestedIndexings = getRequestedIndexings(tender, indexings);
      selectedIndexing.prices?.forEach(price => {
        const answer = tender.tenderAnswers?.find(a => a.questionID === price.questionID);
        if (!answer || !(Number(answer.value) > 0)) return;
        const change = getIndexingPriceChange(selectedIndexing, price.questionID, answer, requestedIndexings);
        rows.push({ tenderName: tender.tenderName, question: price.text, before: change?.before, after: change?.after });
      });
    });
    return rows;
  }, [selectedIndexing, requests, tenders, indexings]);

  const handleCreateIndexing = (indexing: Partial<TenderingIndexingInterface>) => {
    if (editingIndexing) {
      updateTenderingData(prev => ({
        ...prev,
        indexing: (prev.indexing || []).map(i => 
          i.indexingID === editingIndexing.indexingID 
            ? { ...i, ...indexing } 
            : i
        )
      }));
      setEditingIndexing(null);
    } else {
      const newIndexing: TenderingIndexingInterface = {
        ...indexing as TenderingIndexingInterface,
        // Seconds (like the legacy client) to stay within int32 range
        indexingID: Math.floor(Date.now() / 1000),
      };
      
      updateTenderingData(prev => ({
        ...prev,
        indexing: [...(prev.indexing || []), newIndexing]
      }));
      setSelectedIndexingID(newIndexing.indexingID);
    }
  };

  const handleEditIndexing = (indexing: TenderingIndexingInterface) => {
    setEditingIndexing(indexing);
    setIsCreateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingIndexing(null);
  };

  const confirmDelete = () => {
    if (!deletingIndexing) return;
    updateTenderingData(prev => ({
      ...prev,
      indexing: (prev.indexing || []).filter(i => i.indexingID !== deletingIndexing.indexingID)
    }));
    if (selectedIndexingID === deletingIndexing.indexingID) setSelectedIndexingID(null);
    setDeletingIndexing(null);
  };

  const actionButtonClass = "rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200";

  return(
    <div className="p-1 flex flex-col gap-5">
      <Card className="h-fit flex-shrink-0">
        <CardPreview>
          <Title3>Indexeringar</Title3>
        </CardPreview>
        <div>
        <div className="flex mb-3">
          <Button 
            className="w-3xs" 
            appearance="primary" 
            icon={<AddCircleFilled/>}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            Skapa ny indexering
          </Button>
        </div>
        <Divider />
          {indexings.length === 0 ? (
            <Text className="p-2" italic>Det finns inga indexeringar...</Text>
          ) : (
          <Table size="extra-small" aria-label="Indexeringar">
            <TableHeader>
              <TableRow>
                {["Rubrik", "Status", "Öppet", "Gäller från", "Index", "Begärd av", "Åtgärder"].map(label => (
                  <TableHeaderCell key={label}>
                    <Body1Strong>{label}</Body1Strong>
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
            {indexings.map(i => {
              const status = getStatus(i);
              const locked = isLocked(i);
              return (
              <TableRow
                key={i.indexingID}
                className="group cursor-pointer"
                appearance={i.indexingID === selectedIndexingID ? "brand" : "none"}
                onClick={() => setSelectedIndexingID(i.indexingID === selectedIndexingID ? null : i.indexingID)}
              >
                <TableCell>{i.indexingName}</TableCell>
                <TableCell><Badge appearance="tint" color={status.color}>{status.label}</Badge></TableCell>
                <TableCell>{toDateKey(i.startDate)} – {toDateKey(i.endDate)}</TableCell>
                <TableCell>{toDateKey(i.indexingDate)}</TableCell>
                <TableCell>{i.index}</TableCell>
                <TableCell>{getRequests(i.indexingID).length}</TableCell>
                <TableCell>
                  <div className="flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
                    <Button
                      onClick={() => setSelectedIndexingID(i.indexingID === selectedIndexingID ? null : i.indexingID)}
                      icon={<InfoRegular />}
                      appearance="primary"
                      className={actionButtonClass}
                      aria-label="Visa info"
                    />
                    {locked ? (
                      <Tooltip content="Låst – indexeringen har öppnat" relationship="label">
                        <LockClosedRegular fontSize={20} />
                      </Tooltip>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleEditIndexing(i)}
                          icon={<EditRegular />}
                          appearance="primary"
                          className={actionButtonClass}
                          aria-label="Redigera"
                        />
                        <Button
                          onClick={() => setDeletingIndexing(i)}
                          icon={<DeleteRegular />}
                          appearance="primary"
                          className={actionButtonClass}
                          aria-label="Radera"
                        />
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )})}
            </TableBody>
          </Table>
          )}
        </div>
      </Card>
      {selectedIndexing && <Card className="h-fit flex-shrink-0">
        <CardPreview>
          <Title3>Indexering: {selectedIndexing.indexingName}</Title3>
        </CardPreview>
        <div className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <Body1Strong>Beskrivning av indexering:</Body1Strong>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedIndexing.indexingDescription || "" }}
                />
            </div>
            <div className="flex flex-col">
              <Body1Strong>Öppet:</Body1Strong>
              <Text>{toDateKey(selectedIndexing.startDate)} – {toDateKey(selectedIndexing.endDate)}</Text>
            </div>
            <div className="flex flex-col">
              <Body1Strong>Gäller från:</Body1Strong>
              <Text>{toDateKey(selectedIndexing.indexingDate)}</Text>
            </div>
            <div className="flex flex-col">
              <Body1Strong>Index:</Body1Strong>
              <Text>{selectedIndexing.index}</Text>
            </div>
            <div className="flex flex-col">
              <Body1Strong>Avser informationsmängd:</Body1Strong>
              {selectedIndexing.prices?.length ? selectedIndexing.prices.map(p => (
                <div className="flex flex-col mt-3 ml-2" key={p.questionID}>
                  <Caption1 italic>{p.heading}</Caption1>
                  <Body1Strong>{p.text}</Body1Strong>
                </div>
              )) : <Text italic>Ingen information kopplad...</Text>}
            </div>
          </div>
        </div>
      </Card>}
      {selectedIndexing && <Card className="h-fit flex-shrink-0">
        <CardPreview>
          <Title3>Begärda indexeringar</Title3>
        </CardPreview>
        {requests.length === 0 ? (
          <Text className="p-2" italic>Ingen verksamhet har begärt denna indexering.</Text>
        ) : (
        <Table size="extra-small" aria-label="Begärda indexeringar">
          <TableHeader>
            <TableRow>
              {["Verksamhet", "Datum", "Användare", "Kvitto"].map(label => (
                <TableHeaderCell key={label}>
                  <Body1Strong>{label}</Body1Strong>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
          {requests.map(r => (
            <TableRow key={r.receipt || `${r.tenderID}-${r.requestDate}`}>
              <TableCell>{r.tenderName}</TableCell>
              <TableCell>{formatDate(r.requestDate)}</TableCell>
              <TableCell className="break-all">{r.user || "Ej begärd"}</TableCell>
              <TableCell>{r.receipt || "-"}</TableCell>
            </TableRow>
          ))}
          </TableBody>
        </Table>
        )}
      </Card>}
      {selectedIndexing && isDone(selectedIndexing) && <Card className="h-fit flex-shrink-0">
        <CardPreview>
          <Title3>Resultat av indexering</Title3>
        </CardPreview>
        {results.length === 0 ? (
          <Text className="p-2" italic>Inga indexerade priser.</Text>
        ) : (
        <Table size="extra-small" aria-label="Resultat av indexering">
          <TableHeader>
            <TableRow>
              {["Verksamhet", "Pris", "Innan indexering", "Efter indexering"].map(label => (
                <TableHeaderCell key={label}>
                  <Body1Strong>{label}</Body1Strong>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
          {results.map((r, idx) => (
            <TableRow key={idx}>
              <TableCell>{r.tenderName}</TableCell>
              <TableCell>{r.question}</TableCell>
              <TableCell>{r.before ?? "-"}</TableCell>
              <TableCell>{r.after ?? <Text italic>Ej indexerad</Text>}</TableCell>
            </TableRow>
          ))}
          </TableBody>
        </Table>
        )}
      </Card>}

      <CreateIndexing
        open={isCreateDialogOpen}
        parts={tenderingDocument.tenderingParts || []}
        onClose={handleCloseDialog}
        onSave={handleCreateIndexing}
        editingIndexing={editingIndexing}
      />

      <Dialog open={!!deletingIndexing} onOpenChange={(_, data) => !data.open && setDeletingIndexing(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Radera indexering?</DialogTitle>
            <DialogContent>
              Vill du radera indexeringen "{deletingIndexing?.indexingName}"? Ändringen sparas när du klickar på Spara.
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeletingIndexing(null)}>Avbryt</Button>
              <Button appearance="primary" onClick={confirmDelete}>Radera</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

export default TenderingIndexing;
